import { useEffect, useState } from 'react';
import {
  Grid,
  Stack,
  TextField,
  Typography,
  Theme,
  useTheme,
  Box,
  OutlinedInput,
  InputLabel,
  MenuItem,
  FormControl,
  Chip,
} from '@mui/material';
import _without from 'lodash/without';

import Select, { SelectChangeEvent, selectClasses } from '@mui/material/Select';

import Form from 'components/Form';
import { INames } from 'apis/main/type';
import {
  getOMAHAProblem,
  getNandaDiagnosis,
  getOMAHADiagnosis,
} from 'apis/main';

interface Props {
  watch: any;
  setValue: any;
  getValues: any;

  register: any;
  disabled?: boolean;
  omahaDomainNames?: INames[];
  omahaModigiers1Names?: INames[];
  omahaModigiers2Names?: INames[];
  omahaPriorityNames?: INames[];
  isTestMode?: boolean;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function getStyles(name: string, personName: readonly string[], theme: Theme) {
  return {
    fontWeight:
      personName.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

function OMAHA(props: Props) {
  const {
    watch,
    register,
    setValue,
    omahaDomainNames,
    omahaModigiers1Names,
    omahaModigiers2Names,
    omahaPriorityNames,
    disabled,
    getValues,
    isTestMode,
  } = props;

  const m_domain = watch('domain');

  // GetOMAHAProblem
  const m_class = watch('problem');
  const [problemNames, setProblemNames] = useState<INames[]>([]);
  useEffect(() => {
    if (!m_domain || m_domain === 'none') return setProblemNames([]);
    getOMAHAProblem({ domain: m_domain }).then(({ data }) => {
      setProblemNames(data.names);
      setDiagnosisNames([]);
    });
  }, [m_domain, setValue]);

  // GetOMAHA
  const [diagnosisNames, setDiagnosisNames] = useState<INames[]>([]);
  useEffect(() => {
    if (!m_class || m_class === 'none') return setDiagnosisNames([]);
    getOMAHADiagnosis({ domain: m_domain, problem: m_class }).then(
      ({ data }) => {
        setDiagnosisNames(data.names);
      }
    );
  }, [m_domain, m_class, setValue]);

  // Render Select Options
  const getOptionValue = (option: INames) => option.kor;
  const getOptionLabel = (option: INames) => `${option.kor} (${option.eng})`;

  const theme = useTheme();
  const [personName, setPersonName] = useState<string[]>([]);

  const handleChange = (event: SelectChangeEvent<typeof personName>) => {
    const {
      target: { value },
    } = event;
    setPersonName(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value
    );
    setValue('sign_sx', value);
  };

  const handleDelete = (e: React.MouseEvent, value: string) => {
    e.preventDefault();
    setPersonName(current => _without(current, value));
  };

  const handlePreventEvent = (e: React.ClipboardEvent | React.MouseEvent) => {
    if (isTestMode) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <Stack spacing={2}>
      <Grid
        container
        wrap="wrap"
        alignItems="center"
        justifyContent="space-between"
        spacing={0.75}
      >
        <Grid item xs={4}>
          <Typography
            sx={{ lineHeight: '14px', fontSize: '12px', fontWeight: 500 }}
          >
            영역 <br />
            (Domain)
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <Form.MuiSelect
            isNone
            disabled={disabled}
            options={omahaDomainNames}
            getOptionValue={getOptionValue}
            getOptionLabel={getOptionLabel}
            sx={{
              border: '1px solid lightgray',
              borderRadius: '5px',
              fontSize: '12px',
              height: '35px',
              paddingLeft: '5px',
            }}
            {...register('domain')}
          />
        </Grid>
        <Grid item xs={4}>
          <Typography
            sx={{ lineHeight: '14px', fontSize: '12px', fontWeight: 500 }}
          >
            문제
            <br /> (Problem)
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <Form.MuiSelect
            isNone
            disabled={disabled}
            options={problemNames}
            getOptionValue={getOptionValue}
            getOptionLabel={getOptionLabel}
            sx={{
              border: '1px solid lightgray',
              borderRadius: '5px',
              fontSize: '12px',
              height: '35px',
              paddingLeft: '5px',
            }}
            {...register('problem')}
          />
        </Grid>
        <Grid item xs={4}>
          <Typography
            sx={{ lineHeight: '14px', fontSize: '12px', fontWeight: 500 }}
          >
            수정인자 1 <br />
            (Modifier 1)
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <Form.MuiSelect
            isNone
            disabled={disabled}
            options={omahaModigiers1Names}
            getOptionValue={getOptionValue}
            getOptionLabel={getOptionLabel}
            sx={{
              border: '1px solid lightgray',
              borderRadius: '5px',
              fontSize: '12px',
              height: '35px',
              paddingLeft: '5px',
            }}
            {...register('modifier1')}
          />
        </Grid>
        <Grid item xs={4}>
          <Typography
            sx={{ lineHeight: '14px', fontSize: '12px', fontWeight: 500 }}
          >
            수정인자 2 <br />
            (Modifier 2)
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <Form.MuiSelect
            isNone
            disabled={disabled}
            options={omahaModigiers2Names}
            getOptionValue={getOptionValue}
            getOptionLabel={getOptionLabel}
            sx={{
              border: '1px solid lightgray',
              borderRadius: '5px',
              fontSize: '12px',
              height: '35px',
              paddingLeft: '5px',
            }}
            {...register('modifier2')}
          />
        </Grid>
        <Grid item xs={4}>
          <Typography
            sx={{ lineHeight: '14px', fontSize: '12px', fontWeight: 500 }}
          >
            심각성 <br />
            (Priority)
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <Form.MuiSelect
            isNone
            disabled={disabled}
            options={omahaPriorityNames}
            getOptionValue={getOptionValue}
            getOptionLabel={getOptionLabel}
            sx={{
              border: '1px solid lightgray',
              borderRadius: '5px',
              fontSize: '12px',
              height: '35px',
              paddingLeft: '5px',
            }}
            {...register('priority')}
          />
        </Grid>
      </Grid>
      <Grid item xs={4}>
        <Typography
          sx={{ lineHeight: '14px', fontSize: '12px', fontWeight: 500 }}
        >
          징후, 증상 (Sign, Symptom)
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <FormControl sx={{ width: '100%', fontSize: '12px' }}>
          <Select
            labelId="demo-multiple-chip-label"
            id="demo-multiple-chip"
            fullWidth
            multiple
            value={personName}
            onChange={handleChange}
            input={<OutlinedInput id="select-multiple-chip" />}
            renderValue={selected => {
              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value, i) => {
                    return (
                      <>
                        <Chip
                          key={value}
                          label={value}
                          onMouseDown={event => {
                            event.stopPropagation();
                          }}
                          onDelete={e => handleDelete(e, value)}
                          style={{
                            borderRadius: '5px',
                            backgroundColor: 'white',
                            border: '1px solid lightgray',
                            color: '#0000004b',
                          }}
                        />
                      </>
                    );
                  })}
                </Box>
              );
            }}
            MenuProps={MenuProps}
          >
            {diagnosisNames.map(name => (
              <MenuItem
                key={name.kor}
                value={name.kor}
                style={getStyles(name.kor, personName, theme)}
              >
                {name.kor}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={8}>
        <Box
          sx={{
            border: '1px solid #0000004b',
            padding: '10px',
            borderRadius: '5px',
          }}
        >
          <Typography sx={{ fontSize: '12px', color: '#0000005b' }}>
            {personName.map((person, i) => {
              if (i === personName.length - 1) {
                return person;
              }
              return person + ', ';
            })}
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={8}>
        <TextField
          fullWidth
          multiline
          minRows={1}
          size="small"
          variant="outlined"
          disabled={disabled}
          placeholder="이 외의 기타 징후 및 증상을 작성해주세요."
          {...register('sign_sx_etc')}
          onPaste={handlePreventEvent}
          onCopy={handlePreventEvent}
          onContextMenu={handlePreventEvent}
        />
      </Grid>

      <Grid item xs={4}>
        <Typography
          sx={{ lineHeight: '14px', fontSize: '12px', fontWeight: 500 }}
        >
          중재방안 (Intrvention Scheme)
        </Typography>
      </Grid>
      <div style={{ marginBottom: '-5px', marginTop: '10px' }}>
        <Typography
          sx={{
            lineHeight: '14px',
            fontSize: '12px',
            fontWeight: 500,
          }}
        >
          보건교육, 상담과 지도 <br />
          (Teaching, Guidance, and Counseling)
        </Typography>
      </div>

      <Form.Item label="목표 및 대상별 정보">
        <Typography
          sx={{
            lineHeight: '14px',
            fontSize: '12px',
            fontWeight: 400,
            color: '#0000007b',
          }}
        >
          (Targets and Client-Specific Information)
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          variant="outlined"
          disabled={disabled}
          {...register('omaha_target1')}
          onPaste={handlePreventEvent}
          onCopy={handlePreventEvent}
          onContextMenu={handlePreventEvent}
        />
      </Form.Item>

      <Grid item xs={4}>
        <Typography
          sx={{ lineHeight: '14px', fontSize: '12px', fontWeight: 500 }}
        >
          처치와 절차 (Treatments and Procedures)
        </Typography>
      </Grid>
      <div style={{ marginBottom: '-17px', marginTop: '10px' }}>
        <Typography
          sx={{
            lineHeight: '14px',
            fontSize: '12px',
            fontWeight: 400,
            color: '#000000a7',
          }}
        >
          목표 및 대상별 정보
        </Typography>
      </div>

      <Form.Item label="(Targets and Client-Specific Information)">
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          variant="outlined"
          disabled={disabled}
          {...register('omaha_target2')}
          onPaste={handlePreventEvent}
          onCopy={handlePreventEvent}
          onContextMenu={handlePreventEvent}
        />
      </Form.Item>

      <Grid item xs={4}>
        <Typography
          sx={{ lineHeight: '14px', fontSize: '12px', fontWeight: 500 }}
        >
          사례관리 (Case Management)
        </Typography>
      </Grid>
      <div style={{ marginBottom: '-17px', marginTop: '10px' }}>
        <Typography
          sx={{
            lineHeight: '14px',
            fontSize: '12px',
            fontWeight: 400,
            color: '#000000a7',
          }}
        >
          목표 및 대상별 정보
        </Typography>
      </div>

      <Form.Item label="(Targets and Client-Specific Information)">
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          variant="outlined"
          disabled={disabled}
          {...register('omaha_target3')}
          onPaste={handlePreventEvent}
          onCopy={handlePreventEvent}
          onContextMenu={handlePreventEvent}
        />
      </Form.Item>

      <Grid item xs={4}>
        <Typography
          sx={{ lineHeight: '14px', fontSize: '12px', fontWeight: 500 }}
        >
          감독 (Surveillance)
        </Typography>
      </Grid>
      <div style={{ marginBottom: '-17px', marginTop: '10px' }}>
        <Typography
          sx={{
            lineHeight: '14px',
            fontSize: '12px',
            fontWeight: 400,
            color: '#000000a7',
          }}
        >
          목표 및 대상별 정보
        </Typography>
      </div>

      <Form.Item label="(Targets and Client-Specific Information)">
        <TextField
          fullWidth
          multiline
          minRows={5}
          size="small"
          variant="outlined"
          disabled={disabled}
          {...register('omaha_target4')}
          onPaste={handlePreventEvent}
          onCopy={handlePreventEvent}
          onContextMenu={handlePreventEvent}
        />
      </Form.Item>

      <Grid item xs={4}>
        <Typography
          sx={{ lineHeight: '14px', fontSize: '12px', fontWeight: 500 }}
        >
          결과에 대한 문제측정척도 <br />
          (Problem Rating Scale for Outcomes)
        </Typography>
      </Grid>

      <Form.Item label="지식 (Knowledge)">
        <Form.MuiTextField
          sx={{ marginBottom: '10px' }}
          select
          required={false}
          disabled={disabled}
          defaultValue={getValues('omaha_knowledge_selct')}
          {...register('omaha_knowledge_selct')}
        >
          <MenuItem value="지식없음">지식없음</MenuItem>
          <MenuItem value="최소의 지식">최소의 지식</MenuItem>
          <MenuItem value="기본적인 지식">기본적인 지식</MenuItem>
          <MenuItem value=" 적절한 지식"> 적절한 지식</MenuItem>
          <MenuItem value="우수한 지식">우수한 지식</MenuItem>
        </Form.MuiTextField>
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          variant="outlined"
          disabled={disabled}
          {...register('omaha_knowledge')}
          onPaste={handlePreventEvent}
          onCopy={handlePreventEvent}
          onContextMenu={handlePreventEvent}
        />
      </Form.Item>

      <Form.Item label="행동 (Behavior)">
        <Form.MuiTextField
          sx={{ marginBottom: '10px' }}
          select
          required={false}
          disabled={disabled}
          defaultValue={getValues('omaha_behavior_selct')}
          {...register('omaha_behavior_selct')}
        >
          <MenuItem value="부적절한 행위">부적절한 행위</MenuItem>
          <MenuItem value="거의 부적절한 행위">거의 부적절한 행위</MenuItem>
          <MenuItem value="보통의 적절한 행위">보통의 적절한 행위</MenuItem>
          <MenuItem value="대체로 적절한 행위">대체로 적절한 행위</MenuItem>
          <MenuItem value="지속적으로 적절한 행위">
            지속적으로 적절한 행위
          </MenuItem>
        </Form.MuiTextField>
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          variant="outlined"
          disabled={disabled}
          {...register('omaha_behavior')}
          onPaste={handlePreventEvent}
          onCopy={handlePreventEvent}
          onContextMenu={handlePreventEvent}
        />
      </Form.Item>

      <Form.Item label="상태 (Status)">
        <Form.MuiTextField
          sx={{ marginBottom: '10px' }}
          select
          required={false}
          disabled={disabled}
          defaultValue={getValues('omaha_status_selct')}
          {...register('omaha_status_selct')}
        >
          <MenuItem value="극단적인 증상/징후">극단적인 증상/징후</MenuItem>
          <MenuItem value="심각한 증상/징후">심각한 증상/징후</MenuItem>
          <MenuItem value="보통의 증상/징후">보통의 증상/징후</MenuItem>
          <MenuItem value="약간의 증상/징후">약간의 증상/징후</MenuItem>
          <MenuItem value="증상/징후 없음">증상/징후 없음</MenuItem>
        </Form.MuiTextField>
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          variant="outlined"
          disabled={disabled}
          {...register('omaha_status')}
          onPaste={handlePreventEvent}
          onCopy={handlePreventEvent}
          onContextMenu={handlePreventEvent}
        />
      </Form.Item>
    </Stack>
  );
}

export default OMAHA;
